import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IActivityItem,
  IActivityState,
  IActivityPagination,
  IActivityFilters,
  IActivitySort,
  IActivityError,
  IFilterOptions,
  IFilterOptionsRequest,
  IColumnConfig,
  SortBy,
  SortOrder,
} from './activityModels';

const DEFAULT_PAGINATION: IActivityPagination = {
  currentPage: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const DEFAULT_FILTERS: IActivityFilters = {
  customerIds: [],
  siteIds: [],
  activity: [],
  statuses: [],
  userIds: [],
  searchText: '',
  includeQueued: true,
};

const DEFAULT_SORT: IActivitySort = {
  sortBy: SortBy.CreatedAt,
  sortOrder: SortOrder.Desc,
};

export const ALL_COLUMN_IDS = ['description', 'customerName', 'siteName', 'status', 'createdBy', 'createdAt', 'updatedBy', 'updatedAt', 'details'] as const;

export const DEFAULT_COLUMN_CONFIG: IColumnConfig = {
  order: [...ALL_COLUMN_IDS],
  visibility: {
    ...Object.fromEntries(ALL_COLUMN_IDS.map(id => [id, true])),
    customerName: false,
    updatedBy: false,
    updatedAt: false,
  },
};

const initialState: IActivityState = {
  items: [],
  selectedItem: null,
  pagination: DEFAULT_PAGINATION,
  filters: DEFAULT_FILTERS,
  sort: DEFAULT_SORT,
  isLoading: false,
  error: null,
  filterOptions: null,
  filterOptionsLoading: false,
  filterOptionsError: null,
  columnConfig: DEFAULT_COLUMN_CONFIG,
  retryingTaskIds: [],
  retryingWorkflowIds: [],
  hasLoadedOnce: false,
  customerId: '',
  customerIds: [],
  accessibleCustomers: [],
  accessibleCustomersByOrgCache: {},
  accessibleCustomersLoading: false,
  accessibleCustomersError: null,
  pendingToast: null,
  userDisplayNames: {},
  isLoadingUserNames: false,
  userNamesError: false
};

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    fetchActivity: (state, action: PayloadAction<{ customerId?: string; customerIds?: string[]; siteIds?: string[] } | undefined>) => {
      state.isLoading = true;
      state.error = null;
      if (action.payload?.customerId !== undefined) {
        state.customerId = action.payload.customerId;
      }
      if (action.payload?.customerIds !== undefined) {
        state.customerIds = action.payload.customerIds;
        if (action.payload.customerId === undefined) {
          state.customerId = action.payload.customerIds.length === 1 ? action.payload.customerIds[0] : '';
        }
      }
    },
    fetchActivitySuccess: (
      state,
      action: PayloadAction<{ items: IActivityItem[]; pagination: IActivityPagination }>
    ) => {
      state.isLoading = false;
      state.items = action.payload.items;
      state.pagination = action.payload.pagination;
      state.error = null;
      state.hasLoadedOnce = true;
      // Sync selectedItem if modal is open so status changes are reflected immediately
      if (state.selectedItem) {
        const updated = action.payload.items.find(item => item.id === state.selectedItem!.id);
        if (updated) {
          state.selectedItem = updated;
        }
      }
    },
    fetchActivityFailure: (state, action: PayloadAction<IActivityError>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.items = [];
      state.pagination = { ...state.pagination, totalItems: 0, totalPages: 0 };
      // hasLoadedOnce stays false on first-load failure so that:
      // - toolbar remains hidden (no data context to filter/sort)
      // - refresh shows ContentLoader, not skeleton rows
    },
    setFilters: (state, action: PayloadAction<Partial<IActivityFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination = { ...state.pagination, currentPage: 1 };
    },
    // Clears all active filters and returns pagination to page 1.
    // Dispatched by the "Reset Filters" button shown in the empty-state when
    // stale selections produce no results after an auto-refresh.
    resetFilters: state => {
      state.filters = DEFAULT_FILTERS;
      state.pagination = { ...state.pagination, currentPage: 1 };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination = { ...state.pagination, currentPage: action.payload };
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination = { ...state.pagination, pageSize: action.payload, currentPage: 1 };
    },
    setSort: (state, action: PayloadAction<IActivitySort>) => {
      state.sort = action.payload;
      state.pagination = { ...state.pagination, currentPage: 1 };
    },
    setSelectedItem: (state, action: PayloadAction<IActivityItem>) => {
      state.selectedItem = action.payload;
    },
    clearSelectedItem: state => {
      state.selectedItem = null;
    },
    fetchFilterOptions: (state, _action: PayloadAction<IFilterOptionsRequest>) => {
      state.filterOptionsLoading = true;
      state.filterOptionsError = null;
    },
    fetchFilterOptionsSuccess: (state, action: PayloadAction<IFilterOptions>) => {
      state.filterOptionsLoading = false;
      state.filterOptions = action.payload;
      state.filterOptionsError = null;
    },
    fetchFilterOptionsFailure: (state, action: PayloadAction<string>) => {
      state.filterOptionsLoading = false;
      state.filterOptionsError = action.payload;
    },
    fetchAccessibleCustomers: (state, _action: PayloadAction<{ parentOrgId: string }>) => {
      state.accessibleCustomersLoading = true;
      state.accessibleCustomersError = null;
    },
    hydrateAccessibleCustomersFromCache: (
      state,
      action: PayloadAction<{ parentOrgId: string }>
    ) => {
      const cacheMap = state.accessibleCustomersByOrgCache ?? (state.accessibleCustomersByOrgCache = {});
      const cached = cacheMap[action.payload.parentOrgId];
      if (!cached) {
        return;
      }
      state.accessibleCustomers = cached.customers;
      state.accessibleCustomersLoading = false;
      state.accessibleCustomersError = null;
    },
    fetchAccessibleCustomersSuccess: (
      state,
      action: PayloadAction<{ parentOrgId: string; customers: Array<{ id: string; name: string }> }>
    ) => {
      state.accessibleCustomersLoading = false;
      state.accessibleCustomersError = null;
      state.accessibleCustomers = action.payload.customers;
      const cacheMap = state.accessibleCustomersByOrgCache ?? (state.accessibleCustomersByOrgCache = {});
      cacheMap[action.payload.parentOrgId] = {
        fetchedAt: Date.now(),
        customers: action.payload.customers,
      };
    },
    fetchAccessibleCustomersFailure: (state, action: PayloadAction<string>) => {
      state.accessibleCustomersLoading = false;
      state.accessibleCustomersError = action.payload;
      state.accessibleCustomers = [];
    },
    setColumnConfig: (state, action: PayloadAction<IColumnConfig>) => {
      state.columnConfig = action.payload;
    },
    retryTask: (state, action: PayloadAction<string>) => {
      if (!state.retryingTaskIds.includes(action.payload)) {
        state.retryingTaskIds = [...state.retryingTaskIds, action.payload];
      }
    },
    retryTaskSuccess: (state, action: PayloadAction<string>) => {
      state.retryingTaskIds = state.retryingTaskIds.filter(id => id !== action.payload);
    },
    retryTaskFailure: (state, action: PayloadAction<string>) => {
      state.retryingTaskIds = state.retryingTaskIds.filter(id => id !== action.payload);
    },
    retryWorkflow: (state, action: PayloadAction<{ workflowId: string; taskIds: string[] }>) => {
      const { workflowId } = action.payload;
      if (!state.retryingWorkflowIds.includes(workflowId)) {
        state.retryingWorkflowIds = [...state.retryingWorkflowIds, workflowId];
      }
    },
    retryWorkflowDone: (state, action: PayloadAction<string>) => {
      state.retryingWorkflowIds = state.retryingWorkflowIds.filter(id => id !== action.payload);
    },
    resetActivity: (state, action: PayloadAction<{ isSIFlow?: boolean } | undefined>) => {
      const preservedAccessibleCustomers = state.accessibleCustomers;
      const preservedAccessibleCustomersByOrgCache = state.accessibleCustomersByOrgCache;
      const preservedAccessibleCustomersLoading = state.accessibleCustomersLoading;
      const preservedAccessibleCustomersError = state.accessibleCustomersError;

      Object.assign(state, initialState);

      state.accessibleCustomers = preservedAccessibleCustomers;
      state.accessibleCustomersByOrgCache = preservedAccessibleCustomersByOrgCache;
      state.accessibleCustomersLoading = preservedAccessibleCustomersLoading;
      state.accessibleCustomersError = preservedAccessibleCustomersError;
      const isSIFlow = action.payload?.isSIFlow;
      state.columnConfig = {
        ...DEFAULT_COLUMN_CONFIG,
        visibility: {
          ...DEFAULT_COLUMN_CONFIG.visibility,
          customerName: isSIFlow ?? DEFAULT_COLUMN_CONFIG.visibility.customerName,
        },
      };
    },
    queueToast: (state, action: PayloadAction<{ type: 'success' | 'error'; message: string }>) => {
      state.pendingToast = action.payload;
    },
    clearPendingToast: state => {
      state.pendingToast = null;
    },
    fetchUserDisplayNames: (state, _action: PayloadAction<string[]>) => {
      state.isLoadingUserNames = true;
      state.userNamesError = false;
    },
    fetchUserDisplayNamesSuccess: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.isLoadingUserNames = false;
      state.userNamesError = false;
      state.userDisplayNames = { ...state.userDisplayNames, ...action.payload };
    },
    fetchUserDisplayNamesFailure: state => {
      state.isLoadingUserNames = false;
      state.userNamesError = true;
    }
  }
});

export const {
  fetchActivity,
  fetchActivitySuccess,
  fetchActivityFailure,
  setFilters,
  resetFilters,
  setPage,
  setPageSize,
  setSort,
  setSelectedItem,
  clearSelectedItem,
  fetchFilterOptions,
  fetchFilterOptionsSuccess,
  fetchFilterOptionsFailure,
  fetchAccessibleCustomers,
  hydrateAccessibleCustomersFromCache,
  fetchAccessibleCustomersSuccess,
  fetchAccessibleCustomersFailure,
  setColumnConfig,
  retryTask,
  retryTaskSuccess,
  retryTaskFailure,
  retryWorkflow,
  retryWorkflowDone,
  resetActivity,
  queueToast,
  clearPendingToast,
  fetchUserDisplayNames,
  fetchUserDisplayNamesSuccess,
  fetchUserDisplayNamesFailure
} = activitySlice.actions;

export default activitySlice.reducer;
